import React from 'react';
import { Checkbox as AntCheckbox } from 'antd';
import styled from 'styled-components';
import { Checkbox } from './Checkbox';
import { Skeleton } from './Skeleton';
import { colorsPalette } from '../../styles/colors-palette';
import { CSSPixelsNumber } from '../../../infrastructure/types/common/common-types';
import UiKit from '../index';

const DEFAULT_SKELETON_ROW = 10;

type Options<KeyType> = {
  label: string;
  key: KeyType;
  description?: string;
  disabled?: boolean;
};

interface CustomStyles {
  paddingLeft?: CSSPixelsNumber;
  labelColor?: Color;
}

interface CheckboxGroupProps<KeyType> {
  options: Array<Options<KeyType>>;
  customRenderOption?: (index: number, item: Options<KeyType>) => React.ReactNode;
  selectedItems: KeyType[];
  onChange: (selectedKeys: KeyType[]) => void;
  className?: string;
  loading?: boolean;
  pageSize?: PageLimit;
  disabled?: boolean;
  customStyles?: CustomStyles;
}

function CheckboxGroup<KeyType>({
  options,
  onChange,
  className,
  pageSize,
  loading,
  selectedItems,
  customRenderOption,
  disabled,
  customStyles,
}: CheckboxGroupProps<KeyType>): JSX.Element {
  const antOnChange = (checkedValue: KeyType[]) => {
    onChange && onChange(checkedValue);
  };

  const getMockData = ({ size = DEFAULT_SKELETON_ROW }): Array<Options<KeyType>> => {
    return Array.from({ length: size }, (v, i) => {
      return { key: i, label: '' } as Options<KeyType>;
    });
  };

  const renderOptions = () => {
    if (loading) {
      return getMockData({ size: pageSize }).map((item, index) => {
        return <StyledSkeleton key={index} />;
      });
    }

    return options.map((item, index) => {
      if (customRenderOption) {
        return customRenderOption(index, item);
      }

      return (
        <StyledCheckbox
          customstyles={customStyles}
          key={index}
          value={item.key}
          disabled={item.disabled}
        >
          {item.label}

          {item.description && (
            <UiKit.Tooltip text={item.description}>
              <UiKit.icons.QuestionCircle />
            </UiKit.Tooltip>
          )}
        </StyledCheckbox>
      );
    });
  };

  return (
    <StyledGroup
      disabled={disabled}
      className={className}
      onChange={value => antOnChange(value as KeyType[])}
      value={selectedItems}
    >
      {renderOptions()}
    </StyledGroup>
  );
}

const StyledGroup = styled(AntCheckbox.Group)`
  display: flex;
  flex-direction: column;
`;

const StyledCheckbox = styled(Checkbox)<{ customstyles?: CustomStyles }>`
  &.ant-checkbox-wrapper {
    padding-top: 12px;
    padding-left: ${props => props.customstyles?.paddingLeft || '20px'};
    color: ${props => props.customstyles?.labelColor || colorsPalette.gray600};
  }
`;

const StyledSkeleton = styled(Skeleton)`
  padding-top: 12px;
  padding-left: 12px;
`;

export { CheckboxGroup };
